import { type IntlShape } from "react-intl";
import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { getMachineRequestBaseSchema, type MachineRequestBase } from "../machine-request-base";
import { machineCorrugateTrackSchema } from "../utils/machine-corrugate-track";

const x4PhysicalMachineSettingsSchema = z
	.object({
		longHeadParameters: z
			.object({
				maximumPosition: z.number(),
			})
			.passthrough(),
		gantryParameters: z
			.object({
				maximumCorrugateWidth: z.number(),
			})
			.passthrough(),
	})
	.passthrough();

export const getX4MachineRequestSchema = (intl: IntlShape, existingMachines: MachineRequestBase[] = []) => {
	const machineRequestBaseSchema = getMachineRequestBaseSchema(intl, existingMachines);

	return machineRequestBaseSchema
		.extend({
			machineType: z.literal(
				machineTypeSchema.enum.X4,
				intl.formatMessage({ id: "Machine type is invalid", defaultMessage: "Machine type is invalid" }),
			),
			physicalMachineSettings: x4PhysicalMachineSettingsSchema.optional(),
			tracks: z.array(machineCorrugateTrackSchema).optional(),
			printerIp: z.union([machineRequestBaseSchema.shape.ipOrDnsName, z.literal(""), z.null()]),
			printerPort: z.number().int().min(1).max(65535),
			printerEnabled: z.boolean(),
			stopMachineWhenTrackEmpty: z.boolean(),
		})
		.superRefine((request, context) => {
			if (request.printerEnabled && (request.printerIp === null || request.printerIp.trim() === "")) {
				context.addIssue({
					code: "custom",
					path: ["printerIp"],
					message: intl.formatMessage({
						id: "Printer IP Address is required when the printer is enabled",
						defaultMessage: "Printer IP Address is required when the printer is enabled",
					}),
				});
			}
		});
};

export type X4MachineRequest = z.infer<ReturnType<typeof getX4MachineRequestSchema>>;
